import { useEffect, useRef, useCallback } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { ArrowRight, Calculator } from "lucide-react";
import { CreditBalanceCard } from "../components/CreditBalanceCard";

export function CreditsTab({
    user,
    companyAgentUICredit,
    primarySubscription,
    paymentMethods,
    handleGetCompanyAgentUICredit,
    getUser,
    highlightPlatform,
    monthlyCredits,
    onNavigateToCalculator,
    t,
}) {
    // Create refs for each platform card
    const agentuiRef = useRef(null);
    const integrationsRef = useRef(null);
    const openaiRef = useRef(null);

    // Get default payment method
    const defaultPaymentMethod =
        paymentMethods?.find((pm) => pm.isDefault) || paymentMethods?.[0];

    // Refresh credits when tab is opened
    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Scroll to highlighted platform when component mounts
    useEffect(() => {
        if (highlightPlatform) {
            const refMap = {
                agentui: agentuiRef,
                integrations: integrationsRef,
                openai: openaiRef,
            };
            const targetRef = refMap[highlightPlatform];
            if (targetRef?.current) {
                setTimeout(() => {
                    targetRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }, 500); // Delay to ensure tab switch is complete
            }
        }
    }, [highlightPlatform]);

    // Get current balances from user.company.credits
    const getBalance = (platform) => {
        const credit = user?.company?.credits?.find(
            (cc) => cc.platform === platform
        );
        if (!credit) return 0;

        // For agentui, combine subscription and purchased credits
        if (platform === "agentui") {
            return (
                (credit.subscriptionCredits || 0) +
                (credit.purchasedCredits || 0)
            );
        }
        // For others, use the credits field
        return credit.credits || 0;
    };

    const handleRefresh = useCallback(async () => {
        // Refresh both the user object (to update user.company.credits array)
        // and the company credit total (to update the top-level balance)
        await Promise.all([getUser?.(), handleGetCompanyAgentUICredit?.()]);
    }, [getUser, handleGetCompanyAgentUICredit]);
    return (
        <div className="space-y-8">
            {/* How many credits do I need? CTA */}
            {onNavigateToCalculator && (
                <button
                    onClick={onNavigateToCalculator}
                    className="w-full flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 rounded-xl px-5 py-4 border border-indigo-200 transition-colors group"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <Calculator className="w-4.5 h-4.5 text-indigo-600" />
                        </div>
                        <div className="text-left">
                            <span className="text-sm font-semibold text-indigo-700">
                                {t("billing.creditCalculator.howManyCredits") || "How many credits do I need?"}
                            </span>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {t("billing.creditCalculator.howManyCreditsDesc") || "Use our calculator to estimate costs by module"}
                            </p>
                        </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-indigo-400 group-hover:translate-x-0.5 transition-transform" />
                </button>
            )}

            {/* AgentUI Credits */}
            {primarySubscription && (
                <div ref={agentuiRef}>
                    <CreditBalanceCard
                        platform="agentui"
                        platformName="AgentUI"
                        currentBalance={getBalance("agentui")}
                        monthlyCredits={monthlyCredits}
                        paymentMethod={defaultPaymentMethod}
                        onRefresh={handleRefresh}
                        highlight={highlightPlatform === "agentui"}
                        t={t}
                    />
                </div>
            )}

            {/* LLM Credits Section */}
            {primarySubscription && (
                <div className="space-y-8">
                    <h2 className="text-3xl font-bold mb-3">
                        {t("billing.llmCredits") || "LLM Credits"}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        {t("billing.llmCreditsDescription") ||
                            "You can use LLM credits inside your apps or automations."}
                    </p>

                    {/* What are LLM Credits - Q&A */}
                    <div className="border-t border-b border-gray-200 py-6 my-6">
                        <div className="bg-gray-900 text-white rounded-xl p-6">
                            <h3 className="text-lg font-bold mb-3">
                                {t("billing.llmCreditsQA.question") ||
                                    "What are LLM Credits?"}
                            </h3>
                            <p className="text-gray-300 leading-relaxed">
                                {t("billing.llmCreditsQA.answer") ||
                                    "LLM Credits are NOT for code generation. They are specifically for using LLMs (like OpenAI, Gemini, or Perplexity) inside your deployed apps and automations."}
                            </p>
                        </div>
                    </div>

                    {/* Integrations Credits (Image Generation, fal.ai, etc.) */}
                    <div ref={integrationsRef}>
                        <CreditBalanceCard
                            platform="integrations"
                            platformName={
                                t("billing.integrations") || "Integrations"
                            }
                            currentBalance={getBalance("integrations")}
                            paymentMethod={defaultPaymentMethod}
                            onRefresh={handleRefresh}
                            highlight={highlightPlatform === "integrations"}
                            t={t}
                        />
                    </div>
                </div>
            )}

            {/* No subscription message */}
            {!primarySubscription && (
                <Card className="p-8 text-center">
                    <svg
                        className="w-16 h-16 mx-auto text-gray-400 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <h3 className="text-xl font-semibold mb-2">
                        {t("billing.subscriptionRequiredForCredits") ||
                            "Subscription Required"}
                    </h3>
                    <p className="text-gray-600 mb-4">
                        {t("billing.subscriptionRequiredDescription") ||
                            "You need an active subscription to purchase additional credits."}
                    </p>
                </Card>
            )}
        </div>
    );
}
